import React, { FC } from "react";

import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

import Icon from "@mui/icons-material/SubdirectoryArrowRight";

import Actions from "./Actions";
import CertificateStatus from "./CertificateStatus";
import ValidityPeriod from "./ValidityPeriod";
import CourseStatusIcon from "../CourseStatusIcon";
import type { CourseCertificate } from "../../api/compliance";

interface CertificateProps {
  courseCertificate: CourseCertificate;
  coveredByParent?: boolean;
  coveredByParentCourseName?: string;
  variant?: string;
}

const isCoveredStatus = (status: string | null | undefined): boolean => status !== "ok" && status !== "never_expires";

const borderlessCellStyle: React.CSSProperties = { border: 0 };
const iconCellStyle: React.CSSProperties = { width: "1rem" };
const validityPeriodCellStyle: React.CSSProperties = { whiteSpace: "nowrap", width: "15%" };
const validUntilCellStyle: React.CSSProperties = { width: "15%" };
const actionsCellStyle: React.CSSProperties = { width: "12%" };
const iconContainerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  whiteSpace: "nowrap",
};

const Certificate: FC<CertificateProps> = ({
  courseCertificate,
  coveredByParent = false,
  coveredByParentCourseName,
  variant,
}) => {
  const isBorderless = variant === "borderless";
  const rowStyle = isBorderless ? borderlessCellStyle : undefined;
  const getCellStyle = (cellStyle: React.CSSProperties = {}) =>
    isBorderless ? { ...cellStyle, ...borderlessCellStyle } : cellStyle;

  const certificate = courseCertificate.actions?.downloadableCertificate || courseCertificate.certificate;
  if (!certificate) {
    return null;
  }
  const showCoveredByParent = coveredByParent && isCoveredStatus(certificate.status);

  return (
    <TableRow style={rowStyle}>
      <TableCell style={getCellStyle(iconCellStyle)}>
        <div style={iconContainerStyle}>
          {variant === "borderless" && <Icon style={{ marginRight: "0.25rem", fontSize: "1rem" }} />}
          <CourseStatusIcon
            status={certificate.status}
            coveredByParent={showCoveredByParent}
            coveredByParentCourseName={coveredByParentCourseName}
          />
        </div>
      </TableCell>
      <TableCell style={getCellStyle()}>
        ({courseCertificate.type === "custom" ? `cc_${courseCertificate.id}` : courseCertificate.id}){" "}
        {courseCertificate.name}
      </TableCell>
      <TableCell style={getCellStyle(validityPeriodCellStyle)}>
        <ValidityPeriod courseCertificate={courseCertificate} />
      </TableCell>
      <TableCell style={getCellStyle(validUntilCellStyle)}>
        <CertificateStatus
          validUntil={certificate.validUntil}
          status={certificate.status}
          coveredByParent={showCoveredByParent}
        />
      </TableCell>
      <TableCell style={getCellStyle(actionsCellStyle)} align={"justify"}>
        <Actions courseCertificate={courseCertificate} />
      </TableCell>
    </TableRow>
  );
};

export default Certificate;
